<template lang="pug">
div
  j-master-dialog(
    :show="show"
    :master-data="varietyData"
    :loading="loading"
    :is-add="isAdd"
    :delete-loading="deleteLoading"
    :label="dialogLabel"
    :label-name="$t('common.name')"
    :is-on-mobile="isOnMobile"
    :readOnly="readOnly"
    :isMaster="isMaster"
    :hide-fields="['full_name', 'code', 'name', 'keyword']"
    @on-close="closeDialog"
    @on-update="updateVariety"
    @on-create="createVariety"
    type="variety"
    :width="800"
    height="600"
  )
    template(#middle="{ isDisabled }")
      v-row
        v-col(cols="12" sm="6").pr-md-4
          validation-provider(:name="$t('common.code')" :rules="{ required: true, numeric_code_rule: true }" v-slot="{ errors }")
            v-text-field#code.mb-2(
              v-next-focus
              hide-spin-buttons
              :error-messages="errors"
              :value="varietyData.code"
              @input="onCodeInput"
              :label="$t('common.code')"
              :disabled="isDisabled"
            )
              template(v-slot:label)
                span {{$t("common.code")}}
                span.red--text *

          validation-provider(rules="required" v-slot="{ errors }" :name="$t('common.name')")
            v-text-field#name.mb-2(
              v-next-focus
              :error-messages="errors"
              v-model="varietyData.name"
              :label="$t('common.name')"
              :disabled="isDisabled"
            )
              template(v-slot:label)
                span {{$t('common.name')}}
                span.red--text *

          validation-provider(:rules="{ required: true }" v-slot="{ errors }" :name="$t('master.variety.short_name')")
            v-text-field#short_name.mb-2(
              v-next-focus
              :label="$t('master.variety.short_name')"
              :error-messages="errors"
              v-model="varietyData.short_name"
              :disabled="isDisabled"
            )
              template(#label)
                span {{$t('master.variety.short_name')}}
                span.red--text *

          validation-provider(:rules="{ required: true, kana_name_rule: true }" v-slot="{ errors }" :name="$t('common.kana_name')")
            v-text-field#kana_name.mb-2(
              v-next-focus
              :label="$t('common.kana_name')"
              :error-messages="errors"
              v-model="varietyData.kana_name"
              :disabled="isDisabled"
            )
              template(#label)
                span {{$t('common.kana_name')}}
                span.red--text *

          validation-provider(:rules="{ required: true }" v-slot="{ errors }" :name="$t('master.variety.item')")
            v-autocomplete#item_id.mb-2(
              :key="`item-${show}`"
              v-next-focus
              :label="$t('master.variety.item')"
              :items="items"
              :item-text="item => `${item.code}: ${item.name}`"
              item-value="id"
              :error-messages="errors"
              v-model="varietyData.item_id"
              :disabled="isDisabled"
              :no-data-text="$t('message.no_data')"
              clearable
              @change="onItemChange"
            )
              template(#label)
                span {{$t('master.variety.item')}}
                span.red--text *

          .body-1.grey--text.text--darken-1
            span {{ $t('master.variety.is_expenses') }}
          v-radio-group.mt-0(v-model="varietyData.is_expenses" row :disabled="isDisabled")
            v-radio(:label="$t('master.variety.is_expenses_ok')" :value="true" style="width: 120px")
            v-radio(:label="$t('master.variety.is_expenses_ng')" :value="false" style="width: 120px")

        v-col(cols="12" sm="6")
          validation-provider(:rules="{ required: true }" v-slot="{ errors }" :name="$t('master.variety.flower_type')")
            v-autocomplete#flower_type_id.mb-2(
              v-next-focus
              :items="flowerTypeOptions"
              :item-text="item => item.code + ': ' + item.name"
              item-value="id"
              :filter="(item, queryText) => (item.searchstr || '').indexOf(queryText) > -1 || (item.code + ': ' + item.name).indexOf(queryText) > -1"
              :label="$t('master.variety.flower_type')"
              :error-messages="errors"
              v-model="varietyData.flower_type_id"
              :disabled="isDisabled"
              :no-data-text="$t('message.no_data')"
              clearable
            )
              template(#label)
                span {{$t('master.variety.flower_type')}}
                span.red--text *

          v-text-field#agri_item_code.mb-2(
            v-next-focus
            :label="$t('master.variety.agri_item_code')"
            v-model="varietyData.agri_item_code"
            :disabled="isDisabled"
          )

          v-text-field#color_code.mb-2(
            v-next-focus
            :label="$t('master.variety.color_code')"
            v-model="varietyData.color_code"
            :disabled="isDisabled"
          )

          v-text-field#shape_code.mb-2(
            v-next-focus
            :label="$t('master.variety.shape_code')"
            v-model="varietyData.shape_code"
            :disabled="isDisabled"
          )

          v-text-field#keyword.mb-2(
            v-next-focus
            :label="$t('common.keyword')"
            v-model="varietyData.keyword"
            :disabled="isDisabled"
          )

          validation-provider(:name="$t('master.variety.expenses_unit_cost')" :rules="{ numeric: true }" v-slot="{ errors }")
            v-text-field#expenses_unit_cost.mb-2(
              v-next-focus
              :error-messages="errors"
              :label="$t('master.variety.expenses_unit_cost')"
              v-model="varietyData.expenses_unit_cost"
              :disabled="isDisabled"
              @compositionend.native="onExpensesUnitCostCompositionEnd"
              @keyup.enter.native.capture="handleExpensesUnitCostKeydown"
            )

          
</template>
<script>
import {
  defineComponent,
  toRefs,
  ref,
  watch,
  getCurrentInstance,
  computed,
  nextTick
} from 'vue'
import JMasterDialog from '../JMasterDialog/index'
import { api } from '@/plugins'
import { endpoints, showError, toHalfWidthNumber } from '@/utils'
import { useMasterDialog } from '@/components/JMasterDialog/useMasterDialog'
import { useResetData } from '@/composables/useResetData'
import { normalizeCode } from '@/utils/string'

const VarietyDialog = defineComponent({
  components: {
    JMasterDialog
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    variety: {
      type: Object,
      required: false,
      default: null
    },
    isAdd: {
      type: Boolean,
      required: true,
      default: true
    },
    isOnMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    isMaster: {
      type: Boolean,
      default: false
    },
    defaultItemId: {
      type: Number,
      required: false,
      default: null
    },
    defaultCode: {
      type: String,
      required: false,
      default: null
    }
  },
  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const { $toast, $root } = instance.proxy
    const loading = ref(false)
    const deleteLoading = ref(false)
    const { variety, defaultItemId } = toRefs(props)
    const { masterData: varietyData, initializeData } = useMasterDialog()
    const { resetMasterData } = useResetData()
    const items = ref([])
    const flowerTypeOptions = ref([])

    const dialogLabel = computed(() => {
      if (props.isAdd) {
        const item = items.value.find((i) => i.id === varietyData.value.item_id)
        if (item) {
          return `${item.code}:${item.name}${instance.proxy.$t(
            'common.no_ja'
          )}${instance.proxy.$t('master.variety.add')}`
        }
        return instance.proxy.$t('master.variety.add')
      }
      return instance.proxy.$t('master.variety.edit')
    })

    const fetchFlowerTypes = async () => {
      if (flowerTypeOptions.value.length > 0) return
      try {
        const { data } = await api.get(endpoints.MASTER_FLOWER_TYPE, {
          params: { active: true }
        })
        flowerTypeOptions.value = data
      } catch (e) {
        console.error(e)
      }
    }

    const fetchItems = async () => {
      if (items.value.length > 0) return
      try {
        const { data } = await api.get(endpoints.MASTER_ITEM, {
          params: { per_page: 1000, active: true }
        })
        items.value = data
      } catch (e) {
        console.error(e)
      }
    }

    const fetchItemById = async (id) => {
      try {
        const { data } = await api.get(`${endpoints.MASTER_ITEM}/${id}`)
        return data
      } catch (e) {
        console.error(e)
        return null
      }
    }

    const closeDialog = () => {
      loading.value = false
      deleteLoading.value = false
      resetMasterData(initializeData)
      emit('on-close')
    }

    const updateVariety = async (isThrow = false) => {
      loading.value = true
      const id = varietyData.value.id
      if (!id) {
        $toast.error($root.$t('message.update_failed'))
        loading.value = false
        return
      }
      try {
        const params = varietyData.value
        await api.put(`${endpoints.MASTER_VARIETY}/${id}`, params)
        $toast.success($root.$t('message.update_successful'))
        emit('on-reload')
        closeDialog()
      } catch (e) {
        if (isThrow) throw e
        showError(e, $toast, $root.$t('message.update_failed'))
      } finally {
        loading.value = false
      }
    }

    const createVariety = async () => {
      loading.value = true
      try {
        const { data } = await api.post(
          endpoints.MASTER_VARIETY,
          varietyData.value
        )
        $toast.success($root.$t('message.create_successful'))
        emit('on-reload', data)
        closeDialog()
      } catch (e) {
        showError(e, $toast, $root.$t('message.create_failed'))
      } finally {
        loading.value = false
      }
    }

    const onCodeInput = (value) => {
      varietyData.value.code = normalizeCode(value)
    }

    const onItemChange = (val) => {
      const item = items.value.find((i) => i.id === val)
      if (item && item.flower_type_id) {
        varietyData.value.flower_type_id = item.flower_type_id
      }
    }

    watch(
      () => props.show,
      async () => {
        if (props.show) {
          await Promise.all([fetchItems(), fetchFlowerTypes()])

          // Check if the current variety's item_id exists in the loaded items list
          // If not (e.g., the item is inactive), fetch it individually and add it to the list
          if (
            variety.value &&
            variety.value.item_id &&
            !items.value.some((i) => i.id === variety.value.item_id)
          ) {
            const missingItem = await fetchItemById(variety.value.item_id)
            if (missingItem) {
              items.value.push(missingItem)
            }
          }

          let flowerTypeId = null
          if (props.isAdd && defaultItemId.value) {
            const item = items.value.find((i) => i.id === defaultItemId.value)
            // If default item is inactive and not in list yet, fetch it
            if (!item) {
              const missingDefaultItem = await fetchItemById(
                defaultItemId.value
              )
              if (missingDefaultItem) {
                items.value.push(missingDefaultItem)
                flowerTypeId = missingDefaultItem.flower_type_id
              }
            } else {
              flowerTypeId = item.flower_type_id
            }
          }

          const defaultVariety = {
            code: props.defaultCode ?? null,
            name: null,
            short_name: null,
            kana_name: null,
            flower_type_id: flowerTypeId,
            item_id: defaultItemId.value,
            is_expenses: false,
            product_bunrui: null,
            spare_bunrui_1: null,
            spare_bunrui_2: null,
            agri_item_code: null,
            color_code: null,
            shape_code: null,
            keyword: null,
            expenses_unit_cost: null
          }
          initializeData({
            initialData: variety.value,
            defaultData: defaultVariety,
            isAdd: props.isAdd
          })

          // code が自動採番で埋まっている場合(データ連携のF7登録)は、
          // 入力開始点となる「名称」にフォーカスを当てる。
          if (props.isAdd && props.defaultCode) {
            await nextTick()
            const activeDialogs = document.querySelectorAll(
              '.v-dialog.v-dialog--active'
            )
            const dialog = activeDialogs[activeDialogs.length - 1]
            const nameInput = (dialog ?? document).querySelector('#name')
            nameInput?.focus()
          }
        }
      }
    )

    let expensesCompositionJustEnded = false
    const onExpensesUnitCostCompositionEnd = () => {
      expensesCompositionJustEnded = true
    }
    const handleExpensesUnitCostKeydown = (ev) => {
      if (ev.isComposing) return
      const wasIME = expensesCompositionJustEnded
      expensesCompositionJustEnded = false
      const val = String(varietyData.value.expenses_unit_cost ?? '')
      const converted = toHalfWidthNumber(val)
      const convertedStr = converted ?? ''
      if (convertedStr !== val) {
        varietyData.value.expenses_unit_cost = convertedStr || null
        if (!wasIME) ev.stopPropagation()
        // IME確定Enterの場合はstopしない → directives側がjustFinishedComposingをリセットしてくれる
      }
    }

    return {
      closeDialog,
      loading,
      varietyData,
      deleteLoading,
      updateVariety,
      createVariety,
      items,
      flowerTypeOptions,
      onCodeInput,
      dialogLabel,
      onItemChange,
      onExpensesUnitCostCompositionEnd,
      handleExpensesUnitCostKeydown
    }
  }
})

export default VarietyDialog
</script>
